import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Download, FileX2, UserX, Loader2, FileArchive } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from "../ui/alert-dialog";

const ROWS = [["applications", "Tailored applications"], ["career_outputs", "Career Journey notes"], ["saved_questions", "Saved interview questions"],
  ["comments", "Community comments"], ["kesh_chats", "Kesh conversations"], ["scam_reports", "Scam reports you made"], ["files", "Stored files"]];

const Action = ({ icon: Icon, title, desc, children, danger }) => (
  <div className={`rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${danger ? "border-danger/30 bg-danger-soft" : "border-line bg-surface"}`}>
    <Icon className={`w-5 h-5 shrink-0 ${danger ? "text-danger" : "text-brand"}`} />
    <div className="flex-1 min-w-0"><div className="font-semibold">{title}</div><p className="text-sm text-body mt-0.5">{desc}</p></div>
    {children}
  </div>
);

function DeleteAccountDialog({ open, onOpenChange }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const go = async () => {
    setBusy(true);
    try {
      await api.delete("/account", { data: { confirm: text.trim() } });
      localStorage.clear();
      setUser(null);
      toast.success("Your account and data have been deleted");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete your account");
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setText(""); }}>
      <AlertDialogContent className="bg-surface border-line text-ink" data-testid="delete-account-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account permanently?</AlertDialogTitle>
          <AlertDialogDescription className="text-body">
            This removes your profile, Master CV, every application, Career Journey notes, saved questions, comments and Kesh chats. An active monthly plan is cancelled. Payment records are kept for accounting without your name. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label htmlFor="delete-confirm" className="text-sm text-body">Type <span className="font-mono2 text-danger">DELETE</span> to confirm</label>
        <input id="delete-confirm" data-testid="delete-account-confirm-input" value={text} onChange={(e) => setText(e.target.value)} autoComplete="off"
          className="w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-danger" />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-account-cancel" className="bg-transparent border-line text-body hover:bg-subtle hover:text-ink">Keep my account</AlertDialogCancel>
          <button data-testid="delete-account-confirm-button" disabled={text.trim() !== "DELETE" || busy} onClick={go}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-danger text-onbrand text-sm font-semibold hover:bg-danger/90 disabled:opacity-40 transition-colors">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Delete everything
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function PrivacyCentre({ onChanged }) {
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(null);
  const [cvOpen, setCvOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(false);
  const load = () => api.get("/account/summary").then((r) => setSum(r.data)).catch(() => toast.error("Couldn't load your data summary"));
  useEffect(() => { load(); }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.status === 404 ? "Nothing to download yet" : e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(null); }
  };
  const deleteCv = () => run("cv", async () => { await api.delete("/cv"); setCvOpen(false); load(); onChanged?.(); }, "Master CV deleted");

  return (
    <div className="af-fade-up space-y-6" data-testid="privacy-centre">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3"><ShieldCheck className="w-7 h-7 text-brand" /> Privacy & Data</h1>
        <p className="text-body mt-1">See what we store about you, take a copy, or delete it. No email needed.</p>
      </div>

      <div className="rounded-xl border border-line bg-surface p-5" data-testid="privacy-summary">
        <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-3">What we store for you</div>
        {!sum ? <Loader2 className="w-5 h-5 animate-spin text-faint" /> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg bg-canvas border border-line p-3"><div className="font-display text-xl font-bold" data-testid="privacy-count-cv">{sum.has_cv ? "Yes" : "No"}</div><div className="text-xs text-faint">Master CV</div></div>
            {ROWS.map(([k, l]) => (
              <div key={k} className="rounded-lg bg-canvas border border-line p-3"><div className="font-display text-xl font-bold" data-testid={`privacy-count-${k}`}>{sum[k] ?? 0}</div><div className="text-xs text-faint">{l}</div></div>
            ))}
          </div>
        )}
      </div>

      <Action icon={Download} title="Download all my data" desc="A single file (JSON) with your profile, CV text, applications, notes, chats and payment history.">
        <button data-testid="privacy-export-button" disabled={busy === "export"} onClick={() => run("export", () => downloadFile("/account/export", "getinterviewed-my-data.json"), "Your data download has started")}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors shrink-0 disabled:opacity-50">
          {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
        </button>
      </Action>

      <Action icon={FileArchive} title="Download my applications" desc="Every tailored CV, cover letter and job description as PDFs in a ZIP.">
        <button data-testid="privacy-export-zip-button" disabled={busy === "zip"} onClick={() => run("zip", () => downloadFile("/applications/export", "my-applications.zip"))}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors shrink-0 disabled:opacity-50">
          {busy === "zip" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />} Download ZIP
        </button>
      </Action>

      <Action icon={FileX2} title="Delete my Master CV" desc="Removes your uploaded CV and its text. Tailored applications you've already made stay until you delete them.">
        <button data-testid="privacy-delete-cv-button" disabled={!sum?.has_cv} onClick={() => setCvOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-danger/40 text-sm text-danger hover:bg-danger-soft transition-colors shrink-0 disabled:opacity-40">
          <FileX2 className="w-4 h-4" /> Delete CV
        </button>
      </Action>

      <Action danger icon={UserX} title="Delete my account" desc="Permanently erase your account and everything linked to it. You'll be signed out.">
        <button data-testid="privacy-delete-account-button" onClick={() => setAccOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-danger text-onbrand text-sm font-semibold hover:bg-danger/90 transition-colors shrink-0">
          <UserX className="w-4 h-4" /> Delete account
        </button>
      </Action>

      <p className="text-xs text-faint">Deleted data may remain in routine backups for a short time before being overwritten. See our <a href="/data-retention" className="text-brand hover:underline">Data Retention Policy</a>.</p>

      <AlertDialog open={cvOpen} onOpenChange={setCvOpen}>
        <AlertDialogContent className="bg-surface border-line text-ink" data-testid="delete-cv-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your Master CV?</AlertDialogTitle>
            <AlertDialogDescription className="text-body">You'll need to upload a CV again before tailoring new applications.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-line text-body hover:bg-subtle hover:text-ink">Cancel</AlertDialogCancel>
            <button data-testid="delete-cv-confirm-button" disabled={busy === "cv"} onClick={deleteCv}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-danger text-onbrand text-sm font-semibold hover:bg-danger/90 disabled:opacity-40">
              {busy === "cv" && <Loader2 className="w-4 h-4 animate-spin" />} Delete CV
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DeleteAccountDialog open={accOpen} onOpenChange={setAccOpen} />
    </div>
  );
}
